/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

/**
 * Dark Mode Color Calculations
 *
 * Advanced utilities for calculating optimal dark mode colors
 * from light mode colors, ensuring proper contrast and readability.
 */

import { parseHsl, type ColorHsl } from "./color-utils";

export interface DarkModeColorOptions {
  /**
   * Adjust lightness for dark mode (negative = darker, positive = lighter)
   * Default: -15 (makes colors slightly darker for dark mode)
   */
  lightnessAdjustment?: number;
  /**
   * Minimum lightness for dark mode backgrounds
   * Default: 10 (prevents too-dark backgrounds)
   */
  minLightness?: number;
  /**
   * Maximum lightness for dark mode backgrounds
   * Default: 20 (prevents too-light backgrounds)
   */
  maxLightness?: number;
  /**
   * Adjust saturation for dark mode (negative = desaturate, positive = saturate)
   * Default: -10 (slightly desaturate for better readability)
   */
  saturationAdjustment?: number;
  /**
   * Whether to increase contrast for better visibility
   * Default: true
   */
  enhanceContrast?: boolean;
}

const DEFAULT_OPTIONS: Required<DarkModeColorOptions> = {
  lightnessAdjustment: -15,
  minLightness: 10,
  maxLightness: 20,
  saturationAdjustment: -10,
  enhanceContrast: true,
};

/**
 * Calculate optimal dark mode color from a light mode color
 *
 * For dark mode, we want colors that:
 * - Have good contrast against dark backgrounds
 * - Are not too bright (avoid eye strain)
 * - Maintain brand identity (same hue)
 * - Are readable and accessible
 *
 * For brand colors (primary, secondary, accent), we preserve the hue and saturation
 * and only adjust lightness slightly for better contrast, maintaining brand identity.
 */
export function calculateDarkModeColor(
  lightModeHsl: string | ColorHsl,
  options: DarkModeColorOptions = {},
): string {
  const hsl =
    typeof lightModeHsl === "string" ? parseHsl(lightModeHsl) : lightModeHsl;

  // Check if clamping was explicitly requested (not from defaults)
  const hasExplicitClamping =
    "minLightness" in options || "maxLightness" in options;
  const hasExplicitSaturationAdjustment =
    "saturationAdjustment" in options &&
    options.saturationAdjustment !== DEFAULT_OPTIONS.saturationAdjustment;
  const hasExplicitLightnessAdjustment =
    "lightnessAdjustment" in options &&
    options.lightnessAdjustment !== undefined &&
    options.lightnessAdjustment !== DEFAULT_OPTIONS.lightnessAdjustment;

  // For brand colors, we want to maintain the same hue and saturation
  // and only adjust lightness for better visibility on dark backgrounds
  // This preserves brand identity while ensuring good contrast

  // Calculate adjusted lightness - for brand colors, we want them brighter in dark mode
  // to maintain visibility, not darker
  let adjustedLightness = hsl.l;

  if (
    hasExplicitLightnessAdjustment &&
    options.lightnessAdjustment !== undefined &&
    options.lightnessAdjustment !== 0
  ) {
    // Use explicit adjustment if provided and not zero
    adjustedLightness = hsl.l + options.lightnessAdjustment;
  } else {
    // Default behavior for brand colors: make them brighter for dark mode visibility
    // If the color is already dark (lightness < 50), make it brighter for dark mode
    // If the color is light (lightness >= 50), make it slightly brighter but not too much
    if (hsl.l < 50) {
      // Dark colors: increase lightness significantly for visibility
      adjustedLightness = Math.min(70, hsl.l + 20);
    } else {
      // Light colors: increase slightly for better contrast
      adjustedLightness = Math.min(75, hsl.l + 5);
    }
  }

  // Only apply clamping if explicitly requested (for non-brand colors like backgrounds)
  if (hasExplicitClamping) {
    if (options.minLightness !== undefined) {
      adjustedLightness = Math.max(options.minLightness, adjustedLightness);
    }
    if (options.maxLightness !== undefined) {
      adjustedLightness = Math.min(options.maxLightness, adjustedLightness);
    }
  } else {
    // Default: ensure minimum visibility (at least 40% lightness for brand colors)
    // but don't clamp too aggressively
    adjustedLightness = Math.max(40, Math.min(75, adjustedLightness));
  }

  // Preserve saturation for brand colors - only adjust if explicitly requested
  let adjustedSaturation = hsl.s;
  if (hasExplicitSaturationAdjustment) {
    adjustedSaturation = hsl.s + (options.saturationAdjustment ?? 0);
    adjustedSaturation = Math.max(20, Math.min(100, adjustedSaturation));
  }

  // If contrast enhancement is enabled, ensure minimum visibility
  const enhanceContrast =
    options.enhanceContrast ?? DEFAULT_OPTIONS.enhanceContrast;
  if (enhanceContrast && adjustedLightness < 45) {
    adjustedLightness = Math.min(50, adjustedLightness + 5);
  }

  return `${hsl.h} ${Math.round(adjustedSaturation)}% ${Math.round(adjustedLightness)}%`;
}

/**
 * Calculate dark mode foreground color (text on dark backgrounds)
 * Ensures WCAG AA contrast compliance
 */
export function calculateDarkModeForeground(
  backgroundColorHsl: string | ColorHsl,
): string {
  const bg =
    typeof backgroundColorHsl === "string"
      ? parseHsl(backgroundColorHsl)
      : backgroundColorHsl;

  // For very dark backgrounds, use light text
  if (bg.l < 25) {
    return "0 0% 98%"; // Near white
  }

  // For medium-dark backgrounds, use slightly lighter text
  if (bg.l < 40) {
    return "0 0% 95%"; // Very light gray
  }

  // For lighter dark backgrounds, use darker text
  return "0 0% 10%"; // Near black
}

/**
 * Calculate dark mode background colors
 * Generates appropriate background shades for dark mode
 */
export function calculateDarkModeBackgrounds(): {
  background: string;
  card: string;
  popover: string;
  muted: string;
  border: string;
  input: string;
} {
  return {
    // Main background - very dark but not pure black
    background: "0 0% 7%",
    // Card backgrounds - slightly lighter than main
    card: "0 0% 9%",
    // Popover backgrounds - same as card
    popover: "0 0% 9%",
    // Muted backgrounds - subtle distinction
    muted: "0 0% 15%",
    // Borders - visible but not too bright
    border: "0 0% 18%",
    // Input backgrounds - slightly lighter than borders
    input: "0 0% 18%",
  };
}

/**
 * Generate complete dark mode color scheme from light mode colors
 *
 * For brand colors (primary, secondary, accent), we preserve the hue and saturation
 * and only adjust lightness for better contrast, maintaining brand identity.
 */
export function generateDarkModeScheme(lightModeColors: {
  primary: string | ColorHsl;
  secondary?: string | ColorHsl;
  accent?: string | ColorHsl;
}): {
  primary: string;
  primaryForeground: string;
  secondary: string;
  secondaryForeground: string;
  accent: string;
  accentForeground: string;
  background: string;
  foreground: string;
  card: string;
  cardForeground: string;
  border: string;
  input: string;
  muted: string;
  mutedForeground: string;
} {
  // For brand colors, don't clamp lightness - preserve brand identity
  // Only adjust for better visibility on dark backgrounds
  const brandColorOptions: DarkModeColorOptions = {
    lightnessAdjustment: 0, // Let the function calculate optimal lightness
    saturationAdjustment: 0, // Preserve saturation for brand colors
    enhanceContrast: true,
    // Don't set minLightness/maxLightness to allow natural adjustment
  };

  const darkPrimary = calculateDarkModeColor(
    lightModeColors.primary,
    brandColorOptions,
  );
  const darkSecondary = lightModeColors.secondary
    ? calculateDarkModeColor(lightModeColors.secondary, brandColorOptions)
    : darkPrimary;
  const darkAccent = lightModeColors.accent
    ? calculateDarkModeColor(lightModeColors.accent, brandColorOptions)
    : darkPrimary;

  const backgrounds = calculateDarkModeBackgrounds();

  return {
    primary: darkPrimary,
    primaryForeground: calculateDarkModeForeground(parseHsl(darkPrimary)),
    secondary: darkSecondary,
    secondaryForeground: calculateDarkModeForeground(parseHsl(darkSecondary)),
    accent: darkAccent,
    accentForeground: calculateDarkModeForeground(parseHsl(darkAccent)),
    background: backgrounds.background,
    foreground: "0 0% 98%", // Light text on dark backgrounds
    card: backgrounds.card,
    cardForeground: "0 0% 98%",
    border: backgrounds.border,
    input: backgrounds.input,
    muted: backgrounds.muted,
    mutedForeground: "0 0% 70%",
  };
}

/**
 * Check if a color is suitable for dark mode
 * Returns true if color has good contrast against dark backgrounds
 */
export function isSuitableForDarkMode(
  colorHsl: string | ColorHsl,
  minLightness: number = 15,
): boolean {
  const hsl = typeof colorHsl === "string" ? parseHsl(colorHsl) : colorHsl;
  return hsl.l >= minLightness;
}
